import React, { useEffect, useState } from "react";
import { parseKickoff } from "../utils/fixtureStatus";
import {
  Badge,
  Box,
  Flex,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";

const TOP_BAR_BLUE = "#00005f";

function fmtDate(iso) {
  const d = parseKickoff(iso);
  if (!d) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function weekLabel(game, weekPrefix) {
  // NFL post-season rounds are stored as weeks 19-23 by the ingest; the PL has
  // no season_type and just uses matchweeks.
  if (game.season_type === "post") {
    return { 19: "WC", 20: "DIV", 21: "CONF", 22: "PB", 23: "SB" }[game.week] || `Wk ${game.week}`;
  }
  return `${weekPrefix} ${game.week}`;
}

/** Season label differs by sport: NFL sends a year, the PL sends "2023-24". */
function seasonLabel(game) {
  return game.season_name ?? game.season_year ?? "";
}

/** NFL calls it a tie, the PL a draw. */
function isLevel(game) {
  return game.is_draw ?? game.is_tie ?? false;
}

/**
 * Head-to-head history between two teams — fetch plus table, no chrome.
 *
 * Split out from the modal so it can also be dropped straight into another
 * dialog (the PL pick modal renders it under Team Stats) without nesting one
 * modal inside another.
 *
 * `active` gates the fetch so it costs nothing until the section is visible.
 */
export function MatchupHistoryContent({
  teamA,
  teamB,
  seasons = 5,
  fetcher,
  weekPrefix = "Wk",
  drawLabel = "TIE",
  active = true,
  maxH = "260px",
  showRecord = true,
  title,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!active || !teamA?.id || !teamB?.id || !fetcher) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    fetcher(teamA.id, teamB.id, seasons)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || "Unable to load matchup history.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, teamA?.id, teamB?.id, seasons]);

  // Desktop gets full club names and crest logos; on a phone both cost more
  // width than they earn, and squeezed the Season column onto two lines.
  const isDesktop = useBreakpointValue({ base: false, md: true }) ?? false;

  const range = data?.season_range;
  const rec = data?.record;
  const levelCount = rec ? (rec.draws ?? rec.ties ?? 0) : 0;
  const drawHeading = `${drawLabel.charAt(0)}${drawLabel.slice(1).toLowerCase()}s`;

  // Title plus the covered years, e.g. "Matchup History  2017-2026".
  const header = title ? (
    <HStack spacing={2} align="baseline" mb={2}>
      <Text fontWeight="bold" color={TOP_BAR_BLUE}>{title}</Text>
      {range && (
        <Text fontSize="xs" color="gray.500">
          {range.from}-{range.to}
        </Text>
      )}
    </HStack>
  ) : null;

  if (loading) {
    return (
      <>
        {header}
        <Flex align="center" justify="center" py={6}><Spinner size="sm" /></Flex>
      </>
    );
  }
  if (error) {
    return <>{header}<Text color="red.600" fontSize="sm">{error}</Text></>;
  }
  if (!data?.games?.length) {
    return (
      <>
      {header}
      <Flex align="center" justify="center" py={6}>
        <Text color="gray.500" fontSize="sm" textAlign="center">
          No matchup history
          {range ? ` from ${range.from} to ${range.to} seasons` : ""}.
        </Text>
      </Flex>
      </>
    );
  }

  return (
    <VStack align="stretch" spacing={2}>
      {header}
      {showRecord && rec && (
        <Flex justify="center">
          {/* align=stretch is what makes the two club blocks match the height of
              the header+totals rows between them. */}
          <Flex align="stretch" borderRadius="md" overflow="hidden">
            <Flex align="center" px={3} bg={TOP_BAR_BLUE}>
              <Text fontSize="sm" fontWeight="bold" color="white" whiteSpace="nowrap">
                {teamA?.short_name}
              </Text>
            </Flex>

            <SimpleGrid columns={3} spacing={0}>
              {["Wins", drawHeading, "Wins"].map((label, i) => (
                <Box key={`h-${i}`} px={3} py={1}>
                  <Text fontSize="0.6rem" textAlign="center"
                        textTransform="uppercase" whiteSpace="nowrap">
                    {label}
                  </Text>
                </Box>
              ))}
              {[rec.team_a_wins, levelCount, rec.team_b_wins].map((value, i) => (
                <Box key={`v-${i}`} px={3} py={1}>
                  <Text fontSize="sm" fontWeight="bold" textAlign="center">{value}</Text>
                </Box>
              ))}
            </SimpleGrid>

            <Flex align="center" px={3} bg={TOP_BAR_BLUE}>
              <Text fontSize="sm" fontWeight="bold" color="white" whiteSpace="nowrap">
                {teamB?.short_name}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      )}

      {/* Capped so a long rivalry scrolls rather than stretching the dialog. */}
      <Box maxH={maxH} overflowY="auto">
        {/* borderCollapse must be `separate` for the sticky header to paint —
            with Chakra's default collapsed borders the scrolling rows show
            through it. The background also has to sit on each Th rather than
            the Thead, which doesn't paint when positioned. */}
        <Table size="sm" variant="simple" sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <Thead>
            <Tr>
              {[
                { label: "Season", w: { base: "34%", md: "25%" } },
                { label: "Matchup", w: { base: "26%", md: "25%" } },
                { label: "Score", w: { base: "16%", md: "25%" } },
                { label: "Winner", w: { base: "24%", md: "25%" } },
              ].map(({ label, w }) => (
                <Th
                  key={label}
                  px={1}
                  width={w}
                  fontSize="0.6rem"
                  textAlign="center"
                  position="sticky"
                  top={0}
                  zIndex={2}
                  bg="white"
                  borderBottom="2px solid"
                  borderColor="gray.200"
                >
                  {label}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {data.games.map((g) => {
              const level = isLevel(g);
              const winner = level
                ? null
                : g.winner_team_id === g.home_team?.id ? g.home_team : g.away_team;
              return (
                <Tr key={g.game_id}>
                  <Td px={1} py={2} textAlign="center">
                    <Text fontSize="xs">{seasonLabel(g)}</Text>
                    <Text fontSize="0.6rem" color="gray.500" whiteSpace="nowrap">
                      {weekLabel(g, weekPrefix)}
                      {g.season_type === "post" ? "" : ` · ${fmtDate(g.kickoff_utc)}`}
                    </Text>
                  </Td>
                  <Td px={1} py={2} textAlign="center">
                    <HStack spacing={1} justify="center">
                      {isDesktop && g.away_team?.logo_url && (
                        <Image src={g.away_team.logo_url} boxSize="16px" objectFit="contain"
                               flexShrink={0} />
                      )}
                      <Text fontSize="xs">
                        {isDesktop ? g.away_team?.name : g.away_team?.short_name}
                      </Text>
                      <Text fontSize="0.6rem" color="gray.400" flexShrink={0}>@</Text>
                      {isDesktop && g.home_team?.logo_url && (
                        <Image src={g.home_team.logo_url} boxSize="16px" objectFit="contain"
                               flexShrink={0} />
                      )}
                      <Text fontSize="xs">
                        {isDesktop ? g.home_team?.name : g.home_team?.short_name}
                      </Text>
                    </HStack>
                  </Td>
                  <Td px={1} py={2} textAlign="center">
                    <Text fontSize="xs" whiteSpace="nowrap">
                      {g.is_final ? `${g.away_score}-${g.home_score}` : "—"}
                    </Text>
                  </Td>
                  <Td px={1} py={2} textAlign="center">
                    {!g.is_final ? (
                      <Text fontSize="0.65rem" color="gray.400">—</Text>
                    ) : level ? (
                      <Badge colorScheme="orange" fontSize="0.6rem">{drawLabel}</Badge>
                    ) : (
                      <HStack spacing={1} justify="center">
                        {winner?.logo_url && (
                          <Image src={winner.logo_url} boxSize="14px" objectFit="contain" />
                        )}
                        <Text fontSize="xs" fontWeight="bold" color="green.600">
                          {winner?.short_name}
                        </Text>
                      </HStack>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
}

/**
 * Standalone matchup-history dialog. Used by NFL Survivor from the pick cards;
 * the PL pick page renders MatchupHistoryContent inline instead.
 */
export default function MatchupHistoryModal({
  isOpen,
  onClose,
  teamA,
  teamB,
  seasons = 5,
  fetcher,
  weekPrefix = "Wk",
  drawLabel = "TIE",
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
                    borderRadius="lg" maxW="560px">
        <ModalHeader pb={2}>
          <HStack spacing={3} align="center">
            {teamA?.logo_url && <Image src={teamA.logo_url} boxSize="28px" objectFit="contain" />}
            <Text fontSize="md" color={TOP_BAR_BLUE}>
              {teamA?.short_name} vs {teamB?.short_name}
            </Text>
            {teamB?.logo_url && <Image src={teamB.logo_url} boxSize="28px" objectFit="contain" />}
          </HStack>
        </ModalHeader>
        <ModalCloseButton color={TOP_BAR_BLUE} />

        {/* Fixed height so the dialog never grows with the data. */}
        <ModalBody pb={5} minH="240px" maxH="420px">
          <MatchupHistoryContent
            title="Matchup History"
            teamA={teamA}
            teamB={teamB}
            seasons={seasons}
            fetcher={fetcher}
            weekPrefix={weekPrefix}
            drawLabel={drawLabel}
            active={isOpen}
            maxH="330px"
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
